import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import {
  DEFAULT_PARAMS,
  decodeParams,
  encodeParams,
  outputSize,
  sanitizeParams,
  type Params,
} from '../shared/params';
import type { RenderStats } from '../shared/messages';
import { Panel } from './Panel';
import { Viewer } from './Viewer';
import { Dropzone } from './Dropzone';
import { PipelineClient } from './workerClient';
import { getLocale, setLocale, useT } from './i18n';

const GITHUB_URL = 'https://github.com/KohakuBlueleaf/PixelOE';

interface SourceInfo {
  bitmap: ImageBitmap;
  width: number;
  height: number;
  id: string;
}

/** Mirror of `client/demo-gr.py`'s bind behaviour: keep the source aspect ratio. */
function applyAspect(p: Params, aspect: number): Params {
  if (!p.bind || !Number.isFinite(aspect) || aspect <= 0) return p;
  const targetH = Math.max(8, Math.min(1024, Math.round(p.targetW / aspect)));
  return { ...p, targetH };
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function App() {
  const t = useT();
  const [params, setParams] = useState<Params>(() =>
    typeof location !== 'undefined' && location.hash.length > 1
      ? decodeParams(location.hash)
      : { ...DEFAULT_PARAMS },
  );
  const [source, setSource] = useState<SourceInfo | null>(null);
  const [result, setResult] = useState<ImageBitmap | null>(null);
  const [stats, setStats] = useState<RenderStats | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unsupported, setUnsupported] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const clientRef = useRef<PipelineClient | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const aspectRef = useRef(1);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 1600);
  }, []);

  // ------------------------------------------------- latest-wins scheduler
  const pendingRef = useRef<Params | null>(null);
  const inFlightRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasSourceRef = useRef(false);

  const flush = useCallback(() => {
    const next = pendingRef.current;
    if (!next || inFlightRef.current || !hasSourceRef.current) return;
    pendingRef.current = null;
    inFlightRef.current = true;
    setBusy(true);
    clientRef.current?.render(next, true);
  }, []);

  const startRender = useCallback(
    (p: Params, delay = 8) => {
      pendingRef.current = p;
      if (debounceRef.current !== null) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        flush();
      }, delay);
    },
    [flush],
  );

  // ------------------------------------------------------- worker wiring
  useEffect(() => {
    const client = new PipelineClient();
    clientRef.current = client;
    client
      .on('ready', () => {
        /* the first render is kicked off once a source has loaded */
      })
      .on('unsupported', (message) => setUnsupported(message))
      .on('source', (bitmap, width, height, id) => {
        setSource((prev) => {
          prev?.bitmap.close();
          return { bitmap, width, height, id };
        });
        setResult((prev) => {
          prev?.close();
          return null;
        });
        hasSourceRef.current = true;
        aspectRef.current = width / height;
        const next = applyAspect(paramsRef.current, aspectRef.current);
        setParams(next);
        startRender(next, 0);
      })
      .on('frame', (bitmap, s) => {
        setResult((prev) => {
          prev?.close();
          return bitmap;
        });
        setStats(s);
        setBusy(false);
        setProgress(0);
        inFlightRef.current = false;
        if (pendingRef.current) flush();
      })
      .on('progress', (fraction, label) => {
        setProgress(fraction);
        setProgressLabel(label);
      })
      .on('exported', () => {
        // the bytes are consumed by `exportBlob`, which handles saving/copying
        setExporting(false);
      })
      .on('error', (message) => {
        setError(message);
        setBusy(false);
        setExporting(false);
        inFlightRef.current = false;
      });
    client.init();
    return () => {
      client.dispose();
      clientRef.current = null;
    };
  }, [flush, startRender]);

  // ------------------------------------------------------------ mutations
  const update = useCallback(
    (patch: Partial<Params>) => {
      setParams((prev) => {
        const merged = sanitizeParams({ ...prev, ...patch });
        // re-bind the aspect ratio whenever the width changes or binding is on
        const next =
          merged.bind && (patch.targetW !== undefined || patch.bind === true)
            ? applyAspect(merged, aspectRef.current)
            : merged;
        startRender(next);
        return next;
      });
    },
    [startRender],
  );

  const applyPreset = useCallback(
    (p: Params) => {
      const next = applyAspect(p, aspectRef.current);
      setParams(next);
      startRender(next, 0);
    },
    [startRender],
  );

  // ---------------------------------------------------------- url hash
  useEffect(() => {
    const id = setTimeout(() => {
      const hash = encodeParams(params);
      history.replaceState(null, '', hash ? `#${hash}` : location.pathname);
    }, 250);
    return () => clearTimeout(id);
  }, [params]);

  // ------------------------------------------------------- file loading
  const pickFile = useCallback(() => fileInputRef.current?.click(), []);

  const loadFile = useCallback((blob: Blob, name: string) => {
    setError(null);
    hasSourceRef.current = true;
    clientRef.current?.load(blob, `${name}:${blob.size}:${Date.now()}`);
  }, []);

  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            loadFile(file, file.name || 'pasted.png');
            e.preventDefault();
          }
          return;
        }
      }
    };
    const onDragOver = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return;
      e.preventDefault();
      setDragging(true);
    };
    const onDragLeave = (e: DragEvent) => {
      if (e.relatedTarget === null) setDragging(false);
    };
    const onDrop = (e: DragEvent) => {
      const file = e.dataTransfer?.files?.[0];
      setDragging(false);
      if (!file) return;
      e.preventDefault();
      loadFile(file, file.name);
    };
    window.addEventListener('paste', onPaste);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('paste', onPaste);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [loadFile]);

  // ----------------------------------------------------------- actions
  const doExport = useCallback(
    (format: 'image/png' | 'image/webp', copy: boolean) => {
      const client = clientRef.current;
      if (!source || !client) {
        flash(t('drop.needImage'));
        return;
      }
      setExporting(true);
      void client
        .exportBlob(paramsRef.current, format)
        .then(async (blob) => {
          if (copy) {
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
            flash(t('action.copied'));
            return;
          }
          const ext = format === 'image/webp' ? 'webp' : 'png';
          const { w, h } = outputSize(paramsRef.current);
          downloadBlob(blob, `pixeloe-${w}x${h}.${ext}`);
        })
        .catch(() => flash(copy ? t('action.copyFailed') : t('error.title')))
        .finally(() => setExporting(false));
    },
    [source, t, flash],
  );

  const exportPng = useCallback(() => doExport('image/png', false), [doExport]);

  const shareUrl = useCallback(async () => {
    const hash = encodeParams(paramsRef.current);
    const url = `${location.origin}${location.pathname}${hash ? `#${hash}` : ''}`;
    try {
      await navigator.clipboard.writeText(url);
      flash(t('action.urlCopied'));
    } catch {
      history.replaceState(null, '', hash ? `#${hash}` : location.pathname);
      flash(t('action.urlCopied'));
    }
  }, [t, flash]);

  const reset = useCallback(() => {
    const next = applyAspect({ ...DEFAULT_PARAMS }, aspectRef.current);
    setParams(next);
    startRender(next, 0);
  }, [startRender]);

  const localeLabel = useMemo(() => (getLocale() === 'zh' ? 'EN' : '中文'), [t]);
  const out = outputSize(params);

  if (unsupported) {
    return (
      <div class="unsupported">
        <h1>{t('error.unsupported')}</h1>
        <p>{t('error.unsupportedHint')}</p>
        <p style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11.5 }}>{unsupported}</p>
      </div>
    );
  }

  return (
    <div class="app">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.currentTarget.files?.[0];
          if (file) loadFile(file, file.name);
          e.currentTarget.value = '';
        }}
      />
      <header class="header">
        <span class="brand">{t('app.title')}</span>

        <span class="toolbar-sep" />
        <div class="toolbar-group">
          <button class="small" onClick={pickFile} title={t('action.open')}>
            {t('action.open')}
          </button>
          <span class="toolbar-sep" />
          <button class="small primary" disabled={!source || exporting} onClick={exportPng}>
            {exporting ? t('status.exporting') : t('action.exportPng')}
          </button>
          <button
            class="small"
            disabled={!source || exporting}
            onClick={() => doExport('image/webp', false)}
          >
            {t('action.exportWebp')}
          </button>
          <button
            class="small"
            disabled={!source || exporting}
            onClick={() => doExport('image/png', true)}
          >
            {t('action.copy')}
          </button>
          {source && (
            <span class="toolbar-hint" title={t('status.size')}>
              {out.w} × {out.h}
            </span>
          )}
          <span class="toolbar-sep" />
          <button class="small" disabled={!source} onClick={reset}>
            {t('action.reset')}
          </button>
          <button class="small" onClick={() => void shareUrl()}>
            🔗 {t('action.shareUrl')}
          </button>
        </div>

        <span class="spacer" />
        {toast && <span class="badge">{toast}</span>}
        <button
          class="small icon"
          aria-label={t('help.title')}
          title={t('help.title')}
          aria-expanded={helpOpen}
          onClick={() => setHelpOpen((v) => !v)}
        >
          ?
        </button>
        <button
          class="small"
          onClick={() => setLocale(getLocale() === 'zh' ? 'en' : 'zh')}
          title="Language"
        >
          {localeLabel}
        </button>
        <a class="link-button" href={GITHUB_URL} target="_blank" rel="noreferrer noopener">
          {t('app.github')}
        </a>

        {helpOpen && (
          <div class="help-pop" role="dialog" aria-label={t('help.title')}>
            <div class="help-head">
              <b>{t('help.title')}</b>
              <button class="small icon" aria-label={t('action.close')} onClick={() => setHelpOpen(false)}>
                ✕
              </button>
            </div>
            <dl class="help-list">
              <div>
                <dt>
                  <kbd>Space</kbd>
                </dt>
                <dd>{t('help.original')}</dd>
              </div>
              <div>
                <dt>
                  <kbd>F</kbd> / <kbd>双击</kbd>
                </dt>
                <dd>{t('help.fit')}</dd>
              </div>
              <div>
                <dt>
                  <kbd>1</kbd>
                </dt>
                <dd>{t('help.actual')}</dd>
              </div>
              <div>
                <dt>
                  <kbd>+</kbd> <kbd>−</kbd>
                </dt>
                <dd>{t('help.zoom')}</dd>
              </div>
              <div>
                <dt>
                  <kbd>Ctrl</kbd>+<kbd>E</kbd>
                </dt>
                <dd>{t('help.export')}</dd>
              </div>
              <div>
                <dt>
                  <kbd>{t('help.wheel')}</kbd>
                </dt>
                <dd>{t('help.wheelHint')}</dd>
              </div>
              <div>
                <dt>
                  <kbd>{t('help.drag')}</kbd>
                </dt>
                <dd>{t('help.dragHint')}</dd>
              </div>
              <div>
                <dt>
                  <kbd>{t('help.divider')}</kbd>
                </dt>
                <dd>{t('help.dividerHint')}</dd>
              </div>
            </dl>
          </div>
        )}
      </header>

      {error && (
        <div class="error-banner">
          {t('error.title')}: {error}
        </div>
      )}

      <div class="body">
        <Panel params={params} disabled={!source} onChange={update} onPreset={applyPreset} />
        <Viewer
          original={source?.bitmap ?? null}
          result={result}
          stats={stats}
          busy={busy}
          progress={progress}
          progressLabel={progressLabel}
          exporting={exporting}
          onExportShortcut={exportPng}
        >
          <Dropzone onFile={loadFile} onPick={pickFile} dragging={dragging} />
        </Viewer>
      </div>

    </div>
  );
}
